import type { NavigationSuggestedEventData } from "./messageParts";
import type { ArtifactMeta } from "../shared/interfaces";

export type MarvinMarkdownTarget =
    | { kind: "reference-group"; keys: string[] }
    | { kind: "external"; url: string; label: string }
    | { kind: "relative"; url: string }
    | { kind: "unsafe" };

export type MarvinArtifactPresentation = "inline-image" | "inline-html" | "panel";

function referenceKeys(value: string): string[] {
    return value
        .split(",")
        .map((key) => key.trim())
        .filter(Boolean);
}

function safeMarvinExternalUrl(href: string): string | null {
    let parsed: URL;
    try {
        parsed = new URL(href);
    } catch {
        return null;
    }
    if (
        parsed.protocol !== "https:" &&
        parsed.protocol !== "http:" &&
        parsed.protocol !== "mailto:"
    ) {
        return null;
    }
    return parsed.href;
}

export function markdownTarget(href: string | undefined, label: string): MarvinMarkdownTarget {
    if (href?.startsWith("#REFERENCEGROUP")) {
        const keys = referenceKeys(label);
        return keys.length > 0 ? { kind: "reference-group", keys } : { kind: "unsafe" };
    }
    const individual = /^#REFERENCE(?:[_:-]?)(.*)$/i.exec(href ?? "");
    if (individual) {
        const keys = referenceKeys(individual[1]?.trim() || label);
        return keys.length > 0 ? { kind: "reference-group", keys } : { kind: "unsafe" };
    }
    if (href && (/^\/(?!\/)/.test(href) || /^#[A-Za-z0-9_-]+$/.test(href))) {
        return { kind: "relative", url: href };
    }
    const external = href ? safeMarvinExternalUrl(href) : null;
    return external ? { kind: "external", url: external, label } : { kind: "unsafe" };
}

export function artifactPresentation(artifact: ArtifactMeta): MarvinArtifactPresentation {
    if (artifact.displayMode === "inline" && artifact.mimeType.startsWith("image/")) {
        return "inline-image";
    }
    if (artifact.mode === "inline" && artifact.mimeType.startsWith("text/html")) {
        return "inline-html";
    }
    return "panel";
}

/**
 * Whether a navigation card takes the user to its target on arrival: only a `mode: "open"` card
 * they just asked for, once, and never on the Questions page, where the chat is the page.
 */
export function shouldAutoOpenNavigation(
    suggestion: NavigationSuggestedEventData,
    isLiveArrival: boolean,
    alreadyOpened: boolean,
    onQuestionsPage: boolean,
): boolean {
    return isLiveArrival && !alreadyOpened && !onQuestionsPage && suggestion.mode === "open";
}
